<template>
  <article
    :class="['deal-card', { 'deal-card--actionable': isActionable }]"
    @click="fetchExpandedDeal"
  >
    <div class="deal-card__title-wrapper">
      <h3 class="deal-card__title">{{ title }}</h3>
      <DealCardMenu v-if="!isSoldOrInActive" :deal="deal" />
    </div>
    <div class="deal-card__summary">
      <HdIcon v-if="propertyIcon" :src="propertyIcon" height="20" width="20" />
      <ul v-if="summarySection.length" class="deal-card__summary-items">
        <li v-for="(summary, index) in summarySection" :key="`deal-card__summary-items-${index}`">
          {{ summary.label }}
        </li>
      </ul>
    </div>
    <ul v-if="tagsSection.length" class="deal-card__tags">
      <li
        v-for="(tag, index) in tagsSection"
        :key="`deal-card__tags-${index}`"
        class="deal-card__tags-item"
        :class="tag.customClasses"
      >
        {{ tag.label }}
        <HdIcon
          v-if="tag.label === $t('DASHBOARD.TAGS.SOLD')"
          :src="icons.check"
          height="15"
          width="15"
        />
      </li>
    </ul>
    <ul v-if="statusSection.length && !isSoldOrInActive" class="deal-card__status">
      <li v-for="(status, index) in statusSection" :key="`deal-card__status-${index}`">
        <HdIcon :src="status.icon" height="15" width="15" />
        <span>{{ status.data }} {{ status.label }}</span>
      </li>
    </ul>
  </article>
</template>

<script>
/* eslint-disable camelcase */
import { HdIcon } from 'homeday-blocks';
import {
  check,
  event,
  tasks,
  outgoingCall,
  chart,
  apartment,
  house,
  plot,
  city as commercial,
} from 'homeday-assets';
import DealCardMenu from '@/components/dashboard/DealCardMenu.vue';
import { ACTION } from '@/store/CONSTANTS';

export default {
  name: 'DealCard',
  components: {
    HdIcon,
    DealCardMenu,
  },
  props: {
    // TODO: Replace `deal` prop with separate props once all data keys are known.
    deal: {
      type: Object,
      required: true,
    },
    columnName: {
      type: String,
      required: true,
    },
  },
  computed: {
    icons() {
      return {
        chart,
        check,
        event,
        outgoingCall,
        tasks,
        apartment,
        house,
        plot,
        commercial,
      };
    },
    dealUid() {
      return this.deal?.uid;
    },
    dealUidInQuery() {
      return this.$route.query?.uid;
    },
    title() {
      return this.deal?.property?.address?.formatted_address;
    },
    dealStatus() {
      const translationPath = `DASHBOARD.CARD.STATUS.${this.deal?.status?.toUpperCase()}`;
      return this.$te(translationPath) ? this.$t(translationPath) : null;
    },
    listingStatusLabel() {
      if (!this.deal?.listing_status) return null;
      return this.$t(
        `DASHBOARD.CARD.${
          ['online', 'partially_online'].includes(this.deal?.listing_status) ? 'ON' : 'OFF'
        }LINE`,
      );
    },
    isSoldOrInActive() {
      return ['sold', 'inactive'].includes(this.columnName);
    },
    propertyIcon() {
      return this.icons?.[this.deal?.property?.property_type] ?? this.icons.commercial;
    },
    statusSection() {
      const {
        open_tasks_count,
        upcoming_appointments_count,
        open_callback_requests_count,
        open_status_prompts_count,
      } = this.deal;

      return [
        {
          data: open_tasks_count,
          icon: this.icons.tasks,
          label: this.$tc('DASHBOARD.CARD.TASKS', open_tasks_count),
        },
        {
          data: upcoming_appointments_count,
          icon: this.icons.event,
          label: this.$tc('DASHBOARD.CARD.MEETING', upcoming_appointments_count),
        },
        {
          data: open_callback_requests_count,
          icon: this.icons.outgoingCall,
          label: this.$tc('DASHBOARD.CARD.CALLBACK', open_callback_requests_count),
        },
        {
          data: open_status_prompts_count,
          icon: this.icons.chart,
          label: this.$tc('DASHBOARD.CARD.STATUS_QUERY', open_status_prompts_count),
        },
      ].filter((section) => section.data > 0);
    },
    tagsSection() {
      const { days_in_status_group, listing_status } = this.deal;
      const isLost = this.dealStatus === this.$t('DASHBOARD.CARD.STATUS.LOST');
      const baseClass = 'deal-card__tags';

      return [
        {
          label: this.$tc('DASHBOARD.CARD.TIME_ONLINE', days_in_status_group, {
            daysOnline: days_in_status_group,
          }),
          customClasses: {
            [`${baseClass}--blue`]: days_in_status_group < 8,
            [`${baseClass}--yellow`]: days_in_status_group >= 8 && days_in_status_group < 15,
            [`${baseClass}--red`]: days_in_status_group >= 15,
            [`${baseClass}--inactive`]: this.isSoldOrInActive,
          },
        },
        {
          label: this.dealStatus,
          customClasses: {
            [`${baseClass}--green`]: this.isSoldOrInActive && !isLost,
            [`${baseClass}--inactive`]: isLost,
          },
        },
        ...(this.columnName === 'find_and_select_buyer'
          ? [
              {
                label: this.listingStatusLabel,
                customClasses: {
                  [`${baseClass}--onlineGreen`]: listing_status === 'online',
                  [`${baseClass}--yellow`]: listing_status === 'partially_online',
                },
              },
            ]
          : []),
      ].filter((section) => section.label);
    },
    summarySection() {
      const { primary_contact, property } = this.deal;
      const price = property.details.price || property.details?.marketing_price;
      const label = primary_contact?.gender
        ? this.$t(`DASHBOARD.CARD.OWNER_${primary_contact.gender.toUpperCase()}`)
        : this.$t('DASHBOARD.CARD.OWNER_UNKNOWN');
      const salutation = primary_contact?.salutation
        ? primary_contact.salutation
        : this.$t('GENERAL.SALUTATION_UNKNOWN');
      return [
        {
          label: `${label}: ${salutation} ${primary_contact?.first_name} ${primary_contact?.last_name}`,
        },
        {
          label: price ? this.$formatPrice(price) : null,
        },
      ].filter(({ label }) => !!label);
    },
    timeOnlineCustomClasses() {
      const section = this.tagsSection.find(
        (section) =>
          section.label ===
          this.$tc('DASHBOARD.CARD.TIME_ONLINE', this.deal.days_in_status_group, {
            daysOnline: this.deal.days_in_status_group,
          }),
      );

      return section.customClasses;
    },
    isActionable() {
      return this.columnName !== 'inactive';
    },
  },
  async mounted() {
    if (this.dealUidInQuery && this.dealUid === this.dealUidInQuery) {
      await this.$nextTick();
      this.fetchExpandedDeal(this.dealUidInQuery);
    }
  },
  methods: {
    async fetchExpandedDeal() {
      if (!this.isActionable) return;
      const dealUid = this.dealUidInQuery || this.dealUid;
      await this.$store.dispatch(ACTION.DASHBOARD.FETCH_EXPANDED_DEAL, { dealUid });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.deal-card {
  border-radius: $sp-s;
  box-shadow: 0 0 24px rgba(26, 27, 60, 0.16);
  margin-bottom: $sp-m;
  padding: $sp-m;
  width: 368px;

  @media (max-width: 375px) {
    width: calc(100vw - $sp-l - $sp-s);
  }

  @include for('desktop') {
    margin-bottom: $sp-m + $sp-s;
  }

  @include for('desktop-wide') {
    width: 432px;
  }

  &:first-of-type {
    margin-top: $sp-m + $sp-s;
  }

  &__title-wrapper {
    display: flex;
    justify-content: space-between;
    margin-bottom: $sp-s;
    position: relative;
  }

  &__title {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    white-space: normal;
  }

  &__summary {
    display: flex;
    align-items: center;
    margin-bottom: $sp-m;

    svg {
      background-color: getShade($primary-color, 110);
      border-radius: 10px;
      padding: $sp-xxs;
    }

    ::v-deep svg > path {
      fill: $primary-bg;
    }
  }

  &__summary-items,
  &__tags,
  &__status {
    @include font('DS-80');

    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: $sp-xs;
    margin-bottom: $sp-m;
  }

  ul:last-child {
    margin-bottom: 0;
  }

  &__summary-items {
    margin-left: $sp-s;

    li:not(:first-child) {
      margin-left: $sp-m;
      position: relative;

      &:before {
        background-color: getShade($primary-color, 110);
        border-radius: 50%;
        content: '';
        height: $sp-xs;
        left: -10px;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        width: $sp-xs;
      }
    }
  }

  &__tags {
    gap: $sp-s;

    &-item {
      background-color: getShade($quaternary-color, 50);
      border-radius: $sp-s;
      color: $primary-color;
      display: flex;
      padding: $sp-xs $sp-s;
      white-space: normal;

      svg {
        margin-left: $sp-xs;

        path {
          fill: $apple-green;
        }
      }
    }

    &-item.deal-card__tags--inactive {
      background-color: getShade($quaternary-color, 50);
      opacity: 0.75;
    }

    &--blue {
      background-color: getShade($primary-color, 40);
    }

    &--onlineGreen {
      background-color: $apple-green;
      color: $primary-bg;
    }

    &--green {
      background-color: getShade($apple-green, 50);
      border: 1px solid;
    }

    &--yellow {
      color: getShade($tertiary-color, 110);
      background-color: getShade($tertiary-color, 60);
    }

    &--red {
      color: $error-color;
      background-color: getShade($error-color, 50);
    }
  }

  &__status {
    gap: $sp-m;

    > li {
      align-items: center;
      display: flex;
      gap: $sp-xs;
    }
  }

  &--actionable {
    cursor: pointer;
  }
}
</style>
